import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineShareAltIcon from 'LuxKit/Icons/line/LineShareAltIcon'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ShareModal from 'components/Common/Modals/ShareModal/ShareModal'
import type { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import config from 'constants/config'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

const NavOverlay = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${rem(12)} ${rem(20)} ${rem(12)} ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  position: relative;
  z-index: 10;

  ${mediaQueryUp.desktop} {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    padding: ${rem(20)} ${rem(32)} ${rem(20)} ${rem(20)};
    background-color: transparent;
  }
`

const NAV_ANCHORS = ['prizes', 'highlights', 'vip-lounge', 'partners', 'event-details'] as const

const HomeLink = styled(ClickableLink)`
  text-decoration: none;
  color: inherit;
`

interface Props {
  hasRegistration?: boolean
}

function LeLiveNav({ hasRegistration }: Props) {
  const intl = useIntl()

  const navItems = useMemo(() => NAV_ANCHORS.map(anchor => ({
    anchor,
    to: `/live#${anchor}`,
    label: {
      prizes: intl.formatMessage({ id: 'StaPHR200lY1APRNTOiq', defaultMessage: 'Prizes', description: 'Anchor navigation link for the Prizes section on the LE Live event landing page' }),
      highlights: intl.formatMessage({ id: 'jAXgOtTbk-iAyulJvJ02', defaultMessage: 'Highlights', description: 'Anchor navigation link for the Highlights section on the LE Live event landing page' }),
      'vip-lounge': intl.formatMessage({ id: 'ohzJUvqNB2lePvWMEcdZ', defaultMessage: 'VIP lounge', description: 'Anchor navigation link for the VIP lounge section on the LE Live event landing page' }),
      partners: intl.formatMessage({ id: 'r6-5j__i6QVmK1GgsWUm', defaultMessage: 'Partners', description: 'Anchor navigation link for the Partners section on the LE Live event landing page' }),
      'event-details': intl.formatMessage({ id: 'CjdM45m4KfNpJaKuiX4u', defaultMessage: 'Event details', description: 'Anchor navigation link for the Event details section on the LE Live event landing page' }),
    }[anchor],
  })), [intl])

  const shareConfig = useMemo<ShareConfig>(() => ({
    url: `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}/live`,
    shareTitle: intl.formatMessage({
      id: 'KO0vzlKAnmChwN6YXXZG',
      defaultMessage: 'Luxury Escapes Live 2026',
      description: 'Social share title for the LE Live event landing page',
    }),
    shareMessage: intl.formatMessage({
      id: 'Z81-VXjI_ExIu8EAxkGU',
      defaultMessage: 'Check out Luxury Escapes Live 2026 – the ultimate travel event!',
      description: 'Social share message body for the LE Live event landing page',
    }),
    pageType: 'event',
    pageId: 'le-live-2026',
  }), [intl])

  return (
    <NavOverlay>
      <HomeLink to="/">
        <Group direction="horizontal" gap={12} verticalAlign="center">
          <CSSBreakpoint max="tablet">
            <LineArrowLeftIcon colour="neutral-one" />
            <LuxuryEscapesSmallLogo height={28} />
          </CSSBreakpoint>
          <CSSBreakpoint min="desktop">
            <LineArrowLeftIcon colour="neutral-eight" />
            <LuxuryEscapesSmallLogo fill="#f3f1ea" height={28} />
          </CSSBreakpoint>
          <CSSBreakpoint max="tablet">
            <BodyText variant="large" weight="bold" colour="neutral-one">
              {intl.formatMessage({
                id: 'WioYSEBTbtDN5Q4luzSh',
                defaultMessage: 'Home',
                description: 'Navigation bar link label to return to the main homepage from the LE Live event page',
              })}
            </BodyText>
          </CSSBreakpoint>
          <CSSBreakpoint min="desktop">
            <BodyText variant="large" weight="bold" colour="neutral-eight">
              {intl.formatMessage({
                id: 'WioYSEBTbtDN5Q4luzSh',
                defaultMessage: 'Home',
                description: 'Navigation bar link label to return to the main homepage from the LE Live event page',
              })}
            </BodyText>
          </CSSBreakpoint>
        </Group>
      </HomeLink>

      {!hasRegistration && <CSSBreakpoint max="tablet">
        <TextButton
          kind="primary"
          size="small"
          to="/live#registration"
        >
          {intl.formatMessage({
            id: 'wC8u5KQTWhwe1OCdGPux',
            defaultMessage: 'Get tickets',
            description: 'Mobile nav CTA button to scroll to ticket registration',
          })}
        </TextButton>
      </CSSBreakpoint>}

      <CSSBreakpoint min="desktop">
        <Group direction="horizontal" gap={4} verticalAlign="center">
          {navItems.map(item => (
            <TextButton
              key={item.anchor}
              kind="tertiary"
              variant="ghost"
              size="large"
              to={item.to}
            >
              {item.label}
            </TextButton>
          ))}
          <ShareModal shareConfig={shareConfig} hideEmail>
            <TextButton
              kind="secondary"
              variant="ghost"
              size="large"
              startIcon={<LineShareAltIcon />}
            >
              {intl.formatMessage({
                id: 'JJa7P0ZmYMTXi9noy5Us',
                defaultMessage: 'Share',
                description: 'Share button label in the LE Live event desktop navigation bar',
              })}
            </TextButton>
          </ShareModal>
        </Group>
      </CSSBreakpoint>
    </NavOverlay>
  )
}

export default LeLiveNav
